import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Copy, RefreshCw, Trash2, LogOut, Store, Crown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MemberRow } from "@/components/groups/MemberRow";
import { VisibilityPicker } from "@/components/groups/VisibilityPicker";

const Confirm = ({ c, onClose }) => (
  <AlertDialog open={!!c} onOpenChange={(o) => !o && onClose()}>
    <AlertDialogContent data-testid="group-confirm-dialog" className="bg-[#121212] border-[#27272A] text-[#F3F4F6]">
      <AlertDialogHeader>
        <AlertDialogTitle>{c?.title}</AlertDialogTitle>
        <AlertDialogDescription className="text-[#9CA3AF]">{c?.body}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="group-confirm-cancel" className="bg-[#1E1E1E] border-[#27272A] text-[#F3F4F6] hover:bg-[#27272A]">Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid="group-confirm-ok" onClick={c?.run} className="bg-red-600 hover:bg-red-700 text-white">{c?.action}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => api.get(`/groups/${groupId}`).then((r) => setG(r.data)).catch((e) => {
    toast.error(e?.response?.data?.detail || "Couldn't open this group");
    navigate("/groups");
  }), [groupId, navigate]);
  useEffect(() => { load(); }, [load]);

  const call = async (fn, ok, reload = true) => {
    try { await fn(); if (ok) toast.success(ok); if (reload) load(); } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
  };
  const setVisibility = (v) => v !== g.visibility && call(() => api.patch(`/groups/${groupId}`, { visibility: v }), v === "public" ? "Group is now visible to everyone" : "Group is now private");
  const copyCode = () => navigator.clipboard?.writeText(g.invite_code).then(() => toast.success("Invite code copied"));
  const resetCode = () => setConfirm({ title: "Reset the invite code?", body: "The old code stops working right away. Share the new code with people you want to invite.", action: "Reset code",
    run: () => call(() => api.post(`/groups/${groupId}/reset-code`), "New invite code created") });
  const remove = (m) => setConfirm({ title: `Remove ${m.name}?`, body: "They'll lose access to this group's notes. Notes they already bought stay theirs.", action: "Remove",
    run: () => call(() => api.delete(`/groups/${groupId}/members/${m.user_id}`), `${m.name} removed`) });
  const leave = () => setConfirm({ title: "Leave this group?", body: "You'll stop seeing its notes. Anything you bought stays in your workspace.", action: "Leave",
    run: () => call(async () => { await api.delete(`/groups/${groupId}/members/${user.user_id}`); navigate("/groups"); }, "You left the group", false) });
  const del = () => setConfirm({ title: "Delete this group?", body: "Its notes are taken off sale and every member loses access. Buyers keep what they bought. This can't be undone.", action: "Delete group",
    run: () => call(async () => { await api.delete(`/groups/${groupId}`); navigate("/groups"); }, "Group deleted", false) });

  if (!g) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6B7280]" /></div>;
  const pending = g.members.filter((m) => m.status === "pending");
  const active = g.members.filter((m) => m.status === "active");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F3F4F6]" data-testid="group-detail-page">
      <header className="px-4 sm:px-6 py-4 border-b border-[#1E1E1E] flex items-center gap-2 sticky top-0 bg-[#0A0A0A]/90 backdrop-blur z-10 pt-[max(1rem,env(safe-area-inset-top))]">
        <Link to="/groups" data-testid="group-back-link" aria-label="Back" className="h-9 w-9 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#1E1E1E]"><ArrowLeft className="h-4 w-4" /></Link>
        <h1 className="font-heading text-lg font-bold truncate flex items-center gap-2" data-testid="group-name">{g.name}{g.is_admin && <Crown className="h-4 w-4 text-[#F59E0B]" />}</h1>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {g.description && <p className="text-sm text-[#D1D5DB] whitespace-pre-line" data-testid="group-description">{g.description}</p>}
        <Link to={`/market?group=${g.group_id}`} data-testid="group-open-notes-btn"
          className="flex items-center justify-center gap-2 h-11 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-sm font-medium text-white"><Store className="h-4 w-4" /> Browse group notes ({g.note_count})</Link>

        {g.is_admin && (
          <section className="rounded-2xl bg-[#121212] border border-[#1E1E1E] p-4 space-y-2" data-testid="group-visibility-section">
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Who can find this group</div>
            <VisibilityPicker value={g.visibility} onChange={setVisibility} />
          </section>
        )}

        <section className="rounded-2xl bg-[#121212] border border-[#1E1E1E] p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Invite code</div>
          <div className="flex items-center gap-2">
            <code data-testid="group-invite-code" className="flex-1 font-mono text-lg tracking-[0.2em] text-[#F3F4F6]">{g.invite_code}</code>
            <button type="button" onClick={copyCode} data-testid="group-copy-code-btn" className="h-9 px-3 rounded-full bg-[#1E1E1E] text-xs text-[#D1D5DB] flex items-center gap-1.5 hover:text-white"><Copy className="h-3.5 w-3.5" /> Copy</button>
            {g.is_admin && <button type="button" onClick={resetCode} data-testid="group-reset-code-btn" className="h-9 px-3 rounded-full bg-[#1E1E1E] text-xs text-[#D1D5DB] flex items-center gap-1.5 hover:text-white"><RefreshCw className="h-3.5 w-3.5" /> Reset</button>}
          </div>
          <p className="text-[11px] text-[#6B7280]">Anyone with this code can ask to join — the admin approves each request.</p>
        </section>

        {g.is_admin && pending.length > 0 && (
          <section data-testid="group-pending-section" className="space-y-2">
            <h2 className="text-[10px] uppercase tracking-wider text-[#FBBF24]">Waiting for approval ({pending.length})</h2>
            <ul className="rounded-2xl bg-[#121212] border border-[#1E1E1E] divide-y divide-[#1E1E1E]">
              {pending.map((m) => <MemberRow key={m.user_id} m={m} isAdmin
                onApprove={() => call(() => api.post(`/groups/${groupId}/requests/${m.user_id}/approve`), `${m.name} approved`)}
                onReject={() => call(() => api.post(`/groups/${groupId}/requests/${m.user_id}/reject`), "Request declined")} />)}
            </ul>
          </section>
        )}

        <section className="space-y-2">
          <h2 className="text-[10px] uppercase tracking-wider text-[#6B7280]">Members ({active.length})</h2>
          <ul className="rounded-2xl bg-[#121212] border border-[#1E1E1E] divide-y divide-[#1E1E1E]" data-testid="group-members-list">
            {active.map((m) => <MemberRow key={m.user_id} m={m} isAdmin={g.is_admin} onRemove={remove} />)}
          </ul>
        </section>

        {g.is_admin ? (
          <button type="button" onClick={del} data-testid="group-delete-btn" className="w-full h-11 rounded-full border border-[#EF4444]/40 text-sm text-[#F87171] hover:bg-[#EF4444]/10 flex items-center justify-center gap-2"><Trash2 className="h-4 w-4" /> Delete group</button>
        ) : (
          <button type="button" onClick={leave} data-testid="group-leave-btn" className="w-full h-11 rounded-full border border-[#27272A] text-sm text-[#D1D5DB] hover:bg-[#1E1E1E] flex items-center justify-center gap-2"><LogOut className="h-4 w-4" /> Leave group</button>
        )}
      </main>
      <Confirm c={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
